// src/components/partner/ProductAccessPricingCard.tsx

import React, { useMemo } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { LocalOffer, Tune } from '@mui/icons-material';

type SectionWrapperArgs = {
  title: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  description?: string;
  highlighted?: boolean;
  children: React.ReactNode;
};

type Props = {
  onManagePricing: () => void;
  sectionWrapper: (args: SectionWrapperArgs) => React.ReactElement;
};

const ProductAccessPricingCard = ({ onManagePricing, sectionWrapper }: Props) => {
  // Keep the wrapper props stable to reduce re-renders in the parent layout.
  const rightAction = useMemo(
    () => (
      <Button
        variant="contained"
        startIcon={<Tune />}
        onClick={onManagePricing}
        sx={{
          whiteSpace: 'nowrap',
          px: 2.5,
          fontWeight: 600,
        }}
      >
        Manage Pricing
      </Button>
    ),
    [onManagePricing],
  );

  return sectionWrapper({
    title: 'Product Access & Pricing',
    icon: <LocalOffer fontSize="small" />,
    description:
      'Manage product visibility for this partner and configure partner-specific pricing rules.',
    right: rightAction,
    children: (
      <Stack spacing={1} sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary">
          • <b>Visibility</b>: Control whether a product is available to this partner.
        </Typography>

        <Typography variant="body2" color="text.secondary">
          • <b>Partner price</b>: Override default prices on a per-product basis.
        </Typography>

        <Typography variant="body2" color="text.secondary">
          • Bulk updates are supported via <b>Download / Upload</b> on the pricing page.
        </Typography>

        {/* Reserved for future summary data (e.g., visible count, overrides count). */}
        <Box />
      </Stack>
    ),
  });
};

export default ProductAccessPricingCard;
